import moment from 'moment';
import { ConfigConstants } from '../../config/config-constants';
import { ODataObject } from '../../interfaces/odata-model.interface';
import {
    DURATION_ODATA_FORMAT,
    DurationModelOptions,
    DurationModelOptionsFromJson,
    JsonDurationModelOptions,
} from '../options/duration-model-options';
import { PrimitiveModel } from './primitive.model';

export class DurationModel extends PrimitiveModel {
    public constructor(
        name: string,
        public value: moment.Duration | undefined,
        public options = new DurationModelOptions(),
    ) {
        super(name, value, options);
    }

    /**
     * Convert the internal data into an object suitable for OData exchange with SAP
     *
     * @returns {object} an object with 'name' as key and the value converted into a format suitable for SAP
     */
    public getOutModel(): ODataDurationOutModel {
        const outModel: ODataDurationOutModel = {};

        const outputValue = this.value ? this.value : moment.duration(0);
        let result: string;
        switch (this.options.format) {
            case DURATION_ODATA_FORMAT.MINUTES:
                result = outputValue.asMinutes().toString();
                break;
            case DURATION_ODATA_FORMAT.STANDARD:
            default:
                result = moment.utc(outputValue.asMilliseconds()).format(ConfigConstants.formatXSDTimeDuration);
        }

        outModel[this.name] = result;

        return outModel;
    }

    /**
     * Increase the value in this DurationModel
     *
     * @returns {boolean}
     */
    public inc(): boolean {
        if (this.isNotIncrementable()) {
            return false;
        } else {
            // the  operation seem valid, so lets increase the value of the old reference
            if (!this.value) {
                this.value = moment.duration(0);
            }
            this.value.add(this.options.step, this.options.unit);

            return true;
        }
    }

    public isNotIncrementable(): boolean {
        // chec k if we would exceed boundaries with the new possible value
        const newValue = moment.duration(this.value);
        newValue.add(this.options.step, this.options.unit);

        return newValue.asMilliseconds() > this.options.maxValue.asMilliseconds();
    }

    /**
     * Decrease the value in this number
     *
     * @returns {boolean} if decreasing was successful
     */
    public dec(): boolean {
        if (this.isNotDecrementable()) {
            return false;
        } else {
            // the  operation seem valid, so lets increase the value of the old reference
            if (!this.value) {
                this.value = moment.duration(0);
            }
            this.value.subtract(this.options.step, this.options.unit);

            return true;
        }
    }

    public isNotDecrementable(): boolean {
        // chec k if we would exceed boundaries with the new possible value
        const newValue = moment.duration(this.value);
        newValue.subtract(this.options.step, this.options.unit);
        return newValue.asMilliseconds() < this.options.minValue.asMilliseconds();
    }

    /**
     * set the value of the model to a new value, but take step width into account
     *
     * @param {moment.Duration} duration
     * @returns {moment.Duration}
     */
    public set(duration: moment.Duration): moment.Duration {
        // find difference between given duration and nearest step width
        let diff = duration.as(this.options.unit);
        diff = diff % this.options.step;

        const halfDivider = 2;
        // chec k if diff is higher than half the step width to decide if we need to round down or up
        diff =
            diff > this.options.step / halfDivider
                ? this.options.step - diff // roun d up: Add the remaining value to match the next higher step
                : -diff; // roun d down: Set to negative diff to get down to the next smaller step

        // roun d duration to nearest step width according to diff determined above
        duration.add(diff, this.options.unit);

        // find difference between current value and new value
        if (!this.value) {
            // If t he value was null before, then clone the correct duration value and use it.
            this.value = duration.clone();
        } else {
            // If w e had a value before then calculate the difference between original and new value.
            // Add  this value to the current value to keep the reference.
            const differenceOriginalAndNew = (this.value.asMilliseconds() - duration.asMilliseconds()) * -1;
            this.value.add(differenceOriginalAndNew);
        }

        return this.value;
    }
}

export class DurationModelFromJson extends DurationModel {
    public constructor(data: JsonDurationModel) {
        super(
            data.name,
            data.value ? moment.duration(data.value) : moment.duration(0),
            new DurationModelOptionsFromJson(data.options),
        );
    }
}

export interface JsonDurationModel {
    name: string;
    value: string;
    options: JsonDurationModelOptions;
}

export interface ODataDurationOutModel extends ODataObject {
    [key: string]: string;
}
